import { supabase } from '../supabase';
import { isOrgAiEnabledNow, notifyAiDisabled } from '../hooks/useOrgAiEnabled';

// ─── Types ───────────────────────────────────────────────

export interface VoxloudConfig {
  id: string;
  organization_id: string;
  api_key: string;
  is_active: boolean;
  configuration: VoxloudConfigData;
  created_at?: string;
  updated_at?: string;
}

export interface VoxloudConfigData {
  pbx_id: string;
  pbx_label: string;
  phone_number?: string;
  webhook_secret: string;
  auto_create_leads: boolean;
  log_transcriptions: boolean;
  log_recordings: boolean;
  notify_incoming: boolean;
  notify_missed: boolean;
  ai_entity_detect: boolean;
  last_reconcile_at?: string;
  last_sync?: { contacts?: string; history?: string };
  webhook_subscription_ids?: string[];
}

export interface VoxloudUser {
  id: string;
  first_name: string;
  last_name: string;
  extension_number: number;
  outbound_cli?: { outbound_did?: { number: string; label: string }; available_dids?: { number: string; label: string }[] };
}

export interface VoxloudUserMapping {
  id: string;
  organization_id: string;
  config_id: string;
  user_id: string;
  voxloud_extension: string;
  voxloud_user_name: string | null;
  outbound_cli: string | null;
  click_to_call_resource_id: string | null;
  is_active: boolean;
}

export interface WebhookSubscription {
  id: string;
  enabled: boolean;
  event_type: string;
  failed_count: number;
  callback_url: string;
  notification_email?: string;
  last_error?: string;
}

export interface WebhookHealth {
  event_type: string;
  enabled: boolean;
  failed_count: number;
  last_error?: string;
}

export interface SyncResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
}

export interface SyncProgress {
  phase: string;  // 'clients' | 'contacts' | 'done'
  processed: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: number;
}

export interface TestResult {
  ok: boolean;
  error?: string;
  details?: string;
}

export interface VoxloudCallLog {
  id: string;
  voxloud_call_id: string;
  event_type: string;
  direction: string | null;
  caller_number: string | null;
  callee_number: string | null;
  duration: number | null;
  last_event: string | null;
  client_id: string | null;
  client_name?: string;
  user_id: string | null;
  ai_entity_detected: boolean;
  processed: boolean;
  created_at: string;
  recording_detected?: boolean;
}

// ─── Helper: invoke proxy ────────────────────────────────

/**
 * Voxloud è gestito da flusia-core (NON da Supabase Edge Functions).
 * Il frontend chiama `api.flusia.it/api/voxloud`, il gateway forwarda al core.
 *
 * Cambio Wave 5 (2026-05-24): da `supabase.functions.invoke('voxloud-proxy')`
 * a fetch diretto verso il gateway, perché:
 *  - le Edge Functions Supabase Ohio hanno problemi DNS verso `developer.voxloud.com`
 *  - flusia-core ha BullMQ per processing async (reconcile, AI)
 *  - zero cold start, stesso DC del gateway
 */
const GATEWAY_URL = (import.meta.env.VITE_API_GATEWAY_URL as string | undefined) || 'https://api.flusia.it';

async function invokeProxy(body: Record<string, unknown>) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error('Sessione utente non disponibile');

  const res = await fetch(`${GATEWAY_URL}/api/voxloud`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });

  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const message = (data as { error?: string })?.error || `HTTP ${res.status}`;
    throw new Error(message);
  }
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
  return data as Record<string, unknown>;
}

// ─── Service ─────────────────────────────────────────────

export class VoxloudService {

  // ── Config multi-PBX ──────────────────────────────────

  /** Get ALL Voxloud configs for an organization (one per PBX) */
  static async getAllConfigs(organizationId: string): Promise<VoxloudConfig[]> {
    const { data, error } = await supabase
      .from('api_configurations')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('service_name', 'voxloud')
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching Voxloud configs:', error);
      return [];
    }

    return (data || []).map(row => ({
      id: row.id,
      organization_id: row.organization_id,
      api_key: row.api_key,
      is_active: row.is_active,
      configuration: row.configuration || {},
      created_at: row.created_at,
      updated_at: row.updated_at,
    }));
  }

  /** Get a single config by ID */
  static async getConfig(configId: string): Promise<VoxloudConfig | null> {
    const { data, error } = await supabase
      .from('api_configurations')
      .select('*')
      .eq('id', configId)
      .eq('service_name', 'voxloud')
      .single();

    if (error || !data) return null;
    return {
      id: data.id,
      organization_id: data.organization_id,
      api_key: data.api_key,
      is_active: data.is_active,
      configuration: data.configuration || {},
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  }

  /** Test connection with a Voxloud API key (before saving) */
  static async testConnection(organizationId: string, apiKey: string): Promise<{
    valid: boolean;
    error?: string;
    company_name?: string;
    email?: string;
    users?: VoxloudUser[];
    pbx_id?: string;
  }> {
    const data = await invokeProxy({
      action: 'verify',
      organization_id: organizationId,
      api_key: apiKey,
    });
    return data;
  }

  /** Add a new PBX: test connection → save config → register webhooks */
  static async addPbx(organizationId: string, apiKey: string, label: string, phoneNumber?: string): Promise<VoxloudConfig> {
    // 1. Test connection
    const test = await this.testConnection(organizationId, apiKey);
    if (!test.valid) throw new Error(test.error || 'Connessione non valida');

    // 2. Generate webhook secret
    const webhookSecret = crypto.randomUUID();

    // 3. Save config
    const configuration: VoxloudConfigData = {
      pbx_id: test.pbx_id || '',
      pbx_label: label,
      ...(phoneNumber ? { phone_number: phoneNumber } : {}),
      webhook_secret: webhookSecret,
      auto_create_leads: true,
      log_transcriptions: true,
      log_recordings: true,
      notify_incoming: true,
      notify_missed: true,
      ai_entity_detect: true,
    };

    const { data, error } = await supabase
      .from('api_configurations')
      .insert({
        organization_id: organizationId,
        service_name: 'voxloud',
        api_key: apiKey,
        webhook_secret: webhookSecret,
        is_active: true,
        configuration,
      })
      .select()
      .single();

    if (error || !data) throw new Error(error?.message || 'Errore nel salvataggio');

    // 4. Register webhooks via proxy
    try {
      await invokeProxy({
        action: 'register_all_webhooks',
        organization_id: organizationId,
        config_id: data.id,
      });
    } catch (err) {
      console.error('Failed to register webhooks:', err);
      // Non-fatal: config is saved, webhooks can be retried
    }

    return {
      id: data.id,
      organization_id: data.organization_id,
      api_key: data.api_key,
      is_active: data.is_active,
      configuration: data.configuration || {},
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  }

  /** Update PBX configuration (features, label) */
  static async updatePbx(configId: string, updates: Partial<VoxloudConfigData>): Promise<void> {
    const config = await this.getConfig(configId);
    if (!config) throw new Error('Configurazione non trovata');

    const newConfiguration = { ...config.configuration, ...updates };

    const { error } = await supabase
      .from('api_configurations')
      .update({ configuration: newConfiguration })
      .eq('id', configId);

    if (error) throw new Error(error.message);
  }

  /** Remove PBX (soft delete + remove webhooks) */
  static async removePbx(configId: string, organizationId: string): Promise<void> {
    // Remove webhooks first
    try {
      await invokeProxy({
        action: 'remove_all_webhooks',
        organization_id: organizationId,
        config_id: configId,
      });
    } catch (err) {
      console.error('Failed to remove webhooks:', err);
    }

    const { error } = await supabase
      .from('api_configurations')
      .update({ is_active: false })
      .eq('id', configId);

    if (error) throw new Error(error.message);
  }

  // ── User Mappings ─────────────────────────────────────

  /** List Voxloud users from API for a specific PBX */
  static async listVoxloudUsers(configId: string, organizationId: string): Promise<VoxloudUser[]> {
    const data = await invokeProxy({
      action: 'users_list',
      organization_id: organizationId,
      config_id: configId,
    });
    return data.users || [];
  }

  /** Get user mappings for a PBX */
  static async getUserMappings(configId: string): Promise<VoxloudUserMapping[]> {
    const { data, error } = await supabase
      .from('voxloud_user_mappings')
      .select('*')
      .eq('config_id', configId)
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching user mappings:', error);
      return [];
    }
    return data || [];
  }

  /** Save user mappings for a PBX (upsert) */
  static async saveUserMappings(
    configId: string,
    organizationId: string,
    mappings: { voxloud_extension: string; voxloud_user_name: string | null; user_id: string; outbound_cli?: string }[]
  ): Promise<void> {
    // Deactivate all existing mappings for this config
    await supabase
      .from('voxloud_user_mappings')
      .update({ is_active: false })
      .eq('config_id', configId);

    if (mappings.length === 0) return;

    // Upsert new mappings
    const rows = mappings.map(m => ({
      organization_id: organizationId,
      config_id: configId,
      user_id: m.user_id,
      voxloud_extension: m.voxloud_extension,
      voxloud_user_name: m.voxloud_user_name,
      outbound_cli: m.outbound_cli || null,
      is_active: true,
    }));

    const { error } = await supabase
      .from('voxloud_user_mappings')
      .upsert(rows, { onConflict: 'config_id,voxloud_extension' });

    if (error) throw new Error(error.message);
  }

  // ── Contacts Sync ─────────────────────────────────────

  /**
   * Push CRM contacts to Voxloud phonebook in batches.
   * Calls the proxy repeatedly with offset/limit until all contacts are synced.
   * @param onProgress - callback called after each batch with cumulative progress
   */
  static async syncContactsToVoxloud(
    configId: string,
    organizationId: string,
    onProgress?: (progress: SyncProgress) => void,
  ): Promise<SyncResult> {
    const totals: SyncResult = { created: 0, updated: 0, skipped: 0, errors: 0 };
    let phase: string = 'clients';
    let offset = 0;
    const batchSize = 50;

    while (phase !== 'done') {
      const data = await invokeProxy({
        action: 'sync_contacts',
        organization_id: organizationId,
        config_id: configId,
        phase,
        offset,
        batch_size: batchSize,
      });

      // Accumulate counters
      totals.created += (data.clients_created || 0) + (data.contacts_created || 0);
      totals.updated += (data.clients_updated || 0) + (data.contacts_updated || 0);
      totals.skipped += data.skipped || 0;
      totals.errors += data.errors || 0;

      // Report progress
      onProgress?.({
        phase: data.phase || phase,
        processed: data.processed || 0,
        total: data.total || 0,
        ...totals,
      });

      // Move to next batch or phase
      if (data.has_more) {
        offset = data.next_offset || 0;
        phase = data.next_phase || phase;
      } else if (data.next_phase && data.next_phase !== 'done') {
        phase = data.next_phase;
        offset = 0;
      } else {
        phase = 'done';
      }
    }

    return totals;
  }

  // ── Click-to-Call ─────────────────────────────────────

  /** Setup click-to-call resources for all mapped users */
  static async setupClickToCall(configId: string, organizationId: string): Promise<void> {
    await invokeProxy({
      action: 'setup_click_to_call',
      organization_id: organizationId,
      config_id: configId,
    });
  }

  /** Make an outbound call from CRM. Looks up the click-to-call resource_id from the user mapping. */
  static async makeCall(configId: string, organizationId: string, userMappingId: string, phoneNumber: string): Promise<void> {
    // Get the click_to_call_resource_id from the mapping
    const { data: mapping } = await supabase
      .from('voxloud_user_mappings')
      .select('click_to_call_resource_id')
      .eq('id', userMappingId)
      .single();

    if (!mapping?.click_to_call_resource_id) {
      throw new Error('Click-to-call non configurato per questo utente. Configura prima il Click-to-Call nelle impostazioni Voxloud.');
    }

    await invokeProxy({
      action: 'c2c_connect',
      organization_id: organizationId,
      config_id: configId,
      resource_id: mapping.click_to_call_resource_id,
      contact: phoneNumber,
    });
  }

  // ── CDR / Import ──────────────────────────────────────

  /** Import call history from Voxloud CDR API */
  static async importCallHistory(configId: string, organizationId: string, days: number): Promise<ImportResult> {
    const data = await invokeProxy({
      action: 'import_history',
      organization_id: organizationId,
      config_id: configId,
      days,
    });
    return data;
  }

  // ── Webhook Management ────────────────────────────────

  /** List webhook subscriptions for a PBX */
  static async listWebhooks(configId: string, organizationId: string): Promise<WebhookSubscription[]> {
    const data = await invokeProxy({
      action: 'webhooks_list',
      organization_id: organizationId,
      config_id: configId,
    });
    return data.subscriptions || [];
  }

  /** Get webhook health (failed_count, last_error per event type) */
  static async getWebhookHealth(configId: string, organizationId: string): Promise<WebhookHealth[]> {
    const subscriptions = await this.listWebhooks(configId, organizationId);
    return subscriptions.map(s => ({
      event_type: s.event_type,
      enabled: s.enabled,
      failed_count: s.failed_count,
      last_error: s.last_error,
    }));
  }

  /** Re-register all webhooks (useful after URL change or failures) */
  static async reRegisterWebhooks(configId: string, organizationId: string): Promise<void> {
    await invokeProxy({
      action: 'remove_all_webhooks',
      organization_id: organizationId,
      config_id: configId,
    });
    await invokeProxy({
      action: 'register_all_webhooks',
      organization_id: organizationId,
      config_id: configId,
    });
  }

  // ── Webhook Testing ───────────────────────────────────

  /** Send a test webhook event */
  static async sendTestWebhook(configId: string, organizationId: string, eventType: string, phoneNumber: string): Promise<TestResult> {
    const data = await invokeProxy({
      action: 'test_webhook',
      organization_id: organizationId,
      config_id: configId,
      event_type: eventType,
      phone_number: phoneNumber,
    });
    return data;
  }

  /** Get recent call logs for a PBX */
  static async getRecentCallLogs(configId: string, organizationId: string, limit = 20): Promise<VoxloudCallLog[]> {
    const { data, error } = await supabase
      .from('voxloud_call_log')
      .select(`
        id, voxloud_call_id, event_type, direction, caller_number, callee_number,
        duration, last_event, client_id, user_id, ai_entity_detected, processed, created_at
      `)
      .eq('config_id', configId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching call logs:', error);
      return [];
    }
    return data || [];
  }

  /** Check if recording is detected in recent call logs (via raw_payload) */
  static async isRecordingDetected(configId: string): Promise<boolean> {
    const { data } = await supabase
      .from('voxloud_call_log')
      .select('raw_payload')
      .eq('config_id', configId)
      .order('created_at', { ascending: false })
      .limit(20);
    if (!data) return false;
    return data.some((row: { raw_payload?: { data?: { recorded?: boolean } } }) =>
      row.raw_payload?.data?.recorded === true
    );
  }

  // ── Reconciliation ────────────────────────────────────

  /** Trigger reconciliation to catch missed webhooks */
  static async reconcile(configId: string, organizationId: string): Promise<{ recovered: number; already_present: number; errors: number }> {
    const data = await invokeProxy({
      action: 'reconcile',
      organization_id: organizationId,
      config_id: configId,
    });
    return data;
  }

  // ── AI Processing ─────────────────────────────────────

  /** Trigger AI processing on a recording */
  static async processRecordingAI(
    recordingId: string,
    organizationId: string,
    actions: ('transcribe' | 'summarize' | 'entity_detect' | 'sentiment' | 'action_items')[],
    force = false
  ): Promise<Record<string, { success: boolean; error?: string }>> {
    // Interruttore IA dell'azienda spento: nessuna elaborazione, nessun dato al modello.
    if (!isOrgAiEnabledNow()) {
      notifyAiDisabled();
      return {};
    }
    const { data, error } = await supabase.functions.invoke('ai-recording-process', {
      body: {
        recording_id: recordingId,
        organization_id: organizationId,
        actions,
        force,
      },
    });
    if (error) throw new Error(error.message);
    return data?.results || {};
  }
}
